/**
 * Notifications page (GH #2281)
 *
 * The "View all notifications" destination behind the header bell -- the first
 * consumer of the previously-unused `@aaimplatform/ui` NotificationCenter
 * (whose own doc comment names it this destination). Renders the tenant's
 * fi_officer/fi_ops Notification rows from /api/v1/notifications
 * (notification-inbox.openapi.yaml) with category tabs, priority filtering,
 * and mark-read/mark-all-read wired to the real ops. Archive/delete/
 * preferences stay unwired: the canonical spec has no such operations
 * (PE-4/PE-6 scope).
 *
 * Mapping (wire contract -> NotificationCenter's presentation vocabulary):
 *   eventType -> category through EVENT_CATEGORY, one entry per
 *     NotificationEventType value. 'approvals' holds the pledge events that
 *     put a decision in front of the FI (created, signature completed,
 *     acknowledged); 'activity' holds the four pledge outcomes (accepted,
 *     declined, withdrawn, released) and all five participation and
 *     connector events (deal match, funds request, disbursement, connector
 *     authorized, positions attested), per the fi-notification-tabs and
 *     notification-activity-tab-members rulings (b); 'alerts' holds the
 *     events that ask the FI to look at something that went wrong (expiry,
 *     return for correction, collateral health, connector refusal or aging).
 *     A value outside the enum (the spec lets it grow) lands in 'system'.
 *   eventType -> meta-row source label through EVENT_LABEL, never the raw code
 *   severity action_required -> priority 'high'; state_change -> 'normal';
 *            informational -> 'low' (the inbox sort order the spec assigns
 *            severity); the row's badge prints the spec's own severity term
 *            through SEVERITY_BADGE, never the priority word 'High'
 *   category -> icon: an Alerts row carries the warning icon, every other row
 *            the neutral one, so routine intake does not read as urgent as a
 *            RED collateral tier (r3-fi-g8-2)
 *   readAt null -> state 'unread', else 'read'
 */

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { NotificationCenter } from '@aaimplatform/ui';
import type { CenterNotification, NotificationCategory } from '@aaimplatform/ui';
import {
  useNotifications,
  useMarkNotificationRead,
  useMarkAllNotificationsRead,
} from '../hooks/useNotifications';
import type {
  InboxNotification,
  NotificationEventType,
  NotificationSeverity,
} from '../api/services/notifications';

const EVENT_CATEGORY: Record<NotificationEventType, CenterNotification['category']> = {
  pledge_created: 'approvals',
  pledge_signature_completed: 'approvals',
  pledge_acknowledged: 'approvals',
  pledge_accepted: 'activity',
  pledge_declined: 'activity',
  pledge_withdrawn: 'activity',
  pledge_released: 'activity',
  pledge_expired: 'alerts',
  pledge_returned: 'alerts',
  collateral_tier_change: 'alerts',
  collateral_health_warning: 'alerts',
  collateral_source_disconnected: 'alerts',
  collateral_valuation_incomplete: 'alerts',
  pledge_core_reference_drift: 'alerts',
  pledge_release_requested: 'approvals',
  pledge_release_request_denied: 'activity',
  participation_deal_match: 'activity',
  participation_funds_request: 'activity',
  participation_disbursement: 'activity',
  connector_authorized: 'activity',
  connector_attested: 'activity',
  connector_attestation_refused: 'alerts',
  connector_pending_aging: 'alerts',
};

const EVENT_LABEL: Record<NotificationEventType, string> = {
  pledge_created: 'Pledge submitted',
  pledge_signature_completed: 'Pledge signed',
  pledge_acknowledged: 'Pledge acknowledged',
  pledge_accepted: 'Pledge accepted',
  pledge_declined: 'Pledge declined',
  pledge_withdrawn: 'Pledge withdrawn',
  pledge_released: 'Pledge released',
  pledge_expired: 'Pledge expired',
  pledge_returned: 'Pledge returned',
  collateral_tier_change: 'Collateral health',
  collateral_health_warning: 'Collateral health',
  collateral_source_disconnected: 'Collateral source',
  collateral_valuation_incomplete: 'Valuation incomplete',
  pledge_core_reference_drift: 'Core reference missing',
  pledge_release_requested: 'Release requested',
  pledge_release_request_denied: 'Release request denied',
  participation_deal_match: 'Participation match',
  participation_funds_request: 'Participation funding',
  participation_disbursement: 'Participation disbursement',
  connector_authorized: 'Connection authorized',
  connector_attested: 'Positions attested',
  connector_attestation_refused: 'Attestation refused',
  connector_pending_aging: 'Connection pending',
};

/**
 * The row badge, in the spec's NotificationSeverity vocabulary. state_change
 * carries no badge: it maps to the 'normal' priority, which renders none.
 */
const SEVERITY_BADGE: Record<NotificationSeverity, string | undefined> = {
  action_required: 'Action required',
  state_change: undefined,
  informational: 'Informational',
};

/**
 * The tabs this page renders: the categories EVENT_CATEGORY can produce, plus
 * 'system' for an event type outside the enum. Compliance and Mentions stay
 * off because no inbox row can ever land in them (fi-g8-8).
 */
const INBOX_CATEGORIES: NotificationCategory[] = ['all', 'approvals', 'activity', 'alerts', 'system'];

function isEventType(code: string): code is NotificationEventType {
  return Object.prototype.hasOwnProperty.call(EVENT_CATEGORY, code);
}

function toCenterNotification(row: InboxNotification): CenterNotification {
  const known = isEventType(row.eventType);
  const category: CenterNotification['category'] = known ? EVENT_CATEGORY[row.eventType] : 'system';
  const priority =
    row.severity === 'action_required' ? 'high' : row.severity === 'informational' ? 'low' : 'normal';
  const badgeLabel = Object.prototype.hasOwnProperty.call(SEVERITY_BADGE, row.severity)
    ? SEVERITY_BADGE[row.severity]
    : undefined;
  return {
    id: row.id,
    title: row.title,
    message: row.body,
    category,
    priority,
    badgeLabel,
    state: row.readAt ? 'read' : 'unread',
    timestamp: row.createdAt,
    type: category === 'alerts' ? 'warning' : 'info',
    source: known ? EVENT_LABEL[row.eventType] : 'Notification',
    actionHref: row.pledgeId ? `/pledges/${row.pledgeId}` : undefined,
    actionLabel: row.pledgeId ? 'Open pledge' : undefined,
  };
}

export function NotificationsPage(): JSX.Element {
  const navigate = useNavigate();
  // The center is a filter-in-place surface; fetch a full page of history.
  const { data, isLoading, isError } = useNotifications({ pageSize: 100 });
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();

  const notifications: CenterNotification[] = useMemo(
    () => (data?.data ?? []).map(toCenterNotification),
    [data],
  );

  // No padding of its own: the portal layout already insets page content, and
  // a second p-6 pushed this page 48 px right of its siblings (x=40 on phone).
  return (
    <div>
      {isLoading && (
        <p className="text-body-md text-secondary">Loading notifications...</p>
      )}
      {isError && (
        <p className="text-body-md text-error">
          Notifications could not be loaded. Retry shortly; if this persists,
          contact support.
        </p>
      )}
      {!isLoading && !isError && (
        <NotificationCenter
          notifications={notifications}
          /* The server's unread total across every page, the same figure the
             header bell prints (fi-g8-6); the 100 loaded rows are one page. */
          unreadCount={data?.unreadCount}
          categories={INBOX_CATEGORIES}
          /* The portal header already renders "Notifications" as the h1. */
          hideTitle
          onMarkRead={(ids) => ids.forEach((id) => markRead.mutate(id))}
          onMarkAllRead={() => markAllRead.mutate()}
          onNotificationClick={(notification) => {
            markRead.mutate(notification.id);
            if (notification.actionHref) {
              navigate(notification.actionHref);
            }
          }}
          onNotificationAction={(notification) => {
            if (notification.actionHref) {
              navigate(notification.actionHref);
            }
          }}
        />
      )}
    </div>
  );
}
